import type { BrandSurface } from '@/types/portal';

interface BrandSurfacePanelProps {
  surfaces: BrandSurface[];
  canEdit: boolean;
  onToggle: (id: string) => void;
}

export default function BrandSurfacePanel({
  surfaces,
  canEdit,
  onToggle,
}: BrandSurfacePanelProps) {
  const enabledCount = surfaces.filter((surface) => surface.enabled).length;

  return (
    <section className="rounded-lg border border-background-200 bg-background-50">
      <header className="flex items-center justify-between gap-2 border-b border-background-200 px-5 py-4">
        <div>
          <h3 className="font-heading text-sm font-semibold text-foreground-950">Branded surfaces</h3>
          <p className="mt-0.5 text-xs text-foreground-500">
            {enabledCount} of {surfaces.length} active
          </p>
        </div>
        <span className="flex h-8 w-8 items-center justify-center rounded-md bg-accent-100 text-base text-accent-700">
          <i className="ri-brush-line" />
        </span>
      </header>

      <ul className="divide-y divide-background-100">
        {surfaces.map((surface) => (
          <li key={surface.id} className="flex items-start gap-3 px-5 py-3">
            <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-background-100 text-base text-foreground-600">
              <i className={surface.icon} />
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-foreground-900">{surface.label}</p>
              <p className="truncate text-[11px] text-foreground-500">{surface.description}</p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={surface.enabled}
              aria-label={`Toggle ${surface.label}`}
              disabled={!canEdit}
              onClick={() => onToggle(surface.id)}
              className={`relative mt-0.5 h-5 w-9 shrink-0 rounded-full transition-colors cursor-pointer disabled:cursor-not-allowed ${
                surface.enabled ? 'bg-primary-500' : 'bg-background-300'
              }`}
            >
              <span
                className={`absolute top-0.5 h-4 w-4 rounded-full bg-background-50 transition-all ${
                  surface.enabled ? 'left-4' : 'left-0.5'
                }`}
              />
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}